import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../utils/safeToast';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Download,
  Loader2,
  Info,
  Building2,
  User
} from 'lucide-react';

const API = "/api";

const ImportPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setResult(null);
      } else {
        toast.error('Nur CSV-Dateien erlaubt');
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setConfirmOpen(false);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/import/csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setResult(response.data);
      
      if (response.data.success && response.data.imported_properties > 0) {
        toast.success(`${response.data.imported_properties} Objekte erfolgreich importiert!`);
      } else if (response.data.errors.length > 0) {
        toast.warning('Import mit Fehlern abgeschlossen');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import fehlgeschlagen');
      setResult({
        success: false,
        total_rows: 0,
        imported_properties: 0,
        imported_owners: 0,
        skipped_duplicates: 0,
        errors: [{ row: 0, error: error.response?.data?.detail || 'Unbekannter Fehler', data: '' }],
        warnings: []
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `objektname;strasse;ort;eigentuemer_name;eigentuemer_email
Strandhaus Sylt;Strandweg 12;25980 Westerland;Max Mustermann;max@example.de
Dünenblick;Am Strand 5;25980 Kampen;Anna Schmidt;anna@example.de`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'objekte_vorlage.csv';
    link.click();
    toast.success('Vorlage heruntergeladen');
  };

  return (
    <div className="space-y-8" data-testid="import-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            CSV Import
          </h1>
          <p className="text-stone-500 mt-1">Objekte und Eigentümer aus CSV importieren</p>
        </div>
        <Button 
          variant="outline" 
          onClick={downloadTemplate}
          className="rounded-full"
          data-testid="download-template-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Vorlage herunterladen
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Info className="w-6 h-6 text-[#0E4C92] flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h3 className="font-semibold text-[#0E4C92]">CSV-Format</h3>
              <p className="text-sm text-slate-700">
                <strong>Trennzeichen:</strong> Komma (,) oder Semikolon (;) - wird automatisch erkannt
              </p>
              <div className="mt-3">
                <p className="text-sm font-medium text-slate-800 mb-1">Pflichtspalten:</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• objektname</li>
                  <li>• strasse</li>
                  <li>• ort</li>
                  <li>• eigentuemer_name</li>
                  <li>• eigentuemer_email</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card className="bg-white border border-stone-200">
        <CardHeader>
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#0E4C92]" />
            CSV-Datei hochladen
          </CardTitle>
          <CardDescription>
            Wählen Sie eine CSV-Datei aus oder ziehen Sie sie hierher
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive 
                ? 'border-[#0E4C92] bg-blue-50' 
                : 'border-stone-300 hover:border-stone-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-[#0E4C92]' : 'text-stone-400'}`} />
            
            {file ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-slate-900">{file.name}</span>
                  <Badge variant="outline" className="ml-2">
                    {(file.size / 1024).toFixed(1)} KB
                  </Badge>
                </div>
                <div className="flex justify-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFile(null);
                      setResult(null);
                    }}
                    className="rounded-full"
                  >
                    Andere Datei
                  </Button>
                  <Button
                    onClick={() => setConfirmOpen(true)}
                    disabled={uploading}
                    className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                    data-testid="start-import-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importiere...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import starten
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-stone-500">
                  Datei hierher ziehen oder{' '}
                  <label className="text-[#0E4C92] hover:underline cursor-pointer font-medium">
                    durchsuchen
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="csv-file-input"
                    />
                  </label>
                </p>
                <p className="text-sm text-stone-400">Nur .csv Dateien</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className={`border ${result.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              {result.success ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-800">Import erfolgreich</span>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800">Import mit Fehlern</span>
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-slate-900">{result.total_rows}</div>
                <div className="text-sm text-stone-500">Zeilen verarbeitet</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <Building2 className="w-5 h-5 text-[#0E4C92] mx-auto mb-1" />
                <div className="text-2xl font-bold text-emerald-600">{result.imported_properties}</div>
                <div className="text-sm text-stone-500">Objekte importiert</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <User className="w-5 h-5 text-[#0E4C92] mx-auto mb-1" />
                <div className="text-2xl font-bold text-emerald-600">{result.imported_owners}</div>
                <div className="text-sm text-stone-500">Eigentümer erstellt</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">{result.skipped_duplicates}</div>
                <div className="text-sm text-stone-500">Duplikate übersprungen</div>
              </div>
            </div>

            {/* Warnings */}
            {result.warnings.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-amber-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Hinweise ({result.warnings.length})
                </h4>
                <div className="bg-white rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="space-y-1 text-sm text-slate-600">
                    {result.warnings.map((warning, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">•</span>
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Errors */}
            {result.errors.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-red-700 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Fehler ({result.errors.length})
                </h4>
                <div className="bg-white rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="space-y-2 text-sm">
                    {result.errors.map((error, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-red-600">
                        <span className="font-medium">Zeile {error.row}:</span>
                        <span>{error.error}</span>
                        {error.data && <span className="text-stone-500">({error.data})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import starten?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Datei "{file?.name}" wird importiert. Existierende Eigentümer (gleiche E-Mail) werden wiederverwendet. 
              Existierende Objekte (gleicher Name oder Adresse) werden übersprungen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpload}
              className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
              data-testid="confirm-import-btn"
            >
              Importieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ImportPage;
